import type { UpdateResolver } from '@urql/exchange-graphcache'
import type { TypedDocumentNode } from '@urql/next'
import { commentsConfig } from '@/config/comments'
import { graphql } from '@/generated/gql'
import type {
    CommentCreatedSubscription,
    CommentDeletedSubscription,
    CommentReactionsUpdatedSubscription,
    CommentUpdatedSubscription,
    CreateCommentV1Mutation,
    CreateCommentV1MutationVariables,
    MutationDeleteCommentArgs,
} from '@/generated/gql/graphql'
import { GET_CONTENT_ITEM_COMMENTS } from '../hooks/useContentItem/useContentItemCommentsQueries'

const contentItemTypeToTypename = {
    ACTION: 'Action',
    AGENDA: 'AgendaItemType',
    ATTACHMENT: 'Attachment',
    DECISION: 'Decision',
    MOM: 'MOM',
    NOTE: 'Note',
    POLL: 'Poll',
}

type CommentsTotalCountFragment = TypedDocumentNode<{
    id: string
    comments: {
        __typename: 'CommentsConnection'
        totalCount: number
    }
}>

const getCommentsTotalCountFragment = (typename: string) => {
    return `
        fragment CommentsTotalCount on ${typename} {
            id
            comments {
                __typename
                totalCount
            }
        }
    ` as unknown as CommentsTotalCountFragment
}

export const commentsMutationsUpdaters: Record<string, UpdateResolver> = {
    createCommentV1: (
        result: CreateCommentV1Mutation,
        args: CreateCommentV1MutationVariables,
        cache,
    ) => {
        const { comment } = result.createCommentV1
        const { contentId, contentType } = args.input
        const variables = {
            input: {
                contentId,
                contentType,
            },
            last: commentsConfig.pageSize,
        }
        cache.updateQuery(
            {
                query: GET_CONTENT_ITEM_COMMENTS,
                variables,
            },
            (data) => {
                if (data != null) {
                    data.commentsByContentId.nodes.push(comment)
                    return data
                }
                return null
            },
        )
    },
    deleteComment: (_result, args: MutationDeleteCommentArgs, cache) => {
        const { id } = args.input
        cache.invalidate({
            __typename: 'Comment',
            id,
        })
    },
}

export const commentsSubscriptionsUpdaters: Record<string, UpdateResolver> = {
    commentCreated: (result: CommentCreatedSubscription, _args, cache) => {
        const {
            comment: { contentId, contentType },
        } = result.commentCreated
        cache.invalidate('Query', 'commentsByContentId', {
            input: { contentId, contentType },
            last: commentsConfig.pageSize,
        })
        const typename = contentItemTypeToTypename[contentType]
        const fragment = getCommentsTotalCountFragment(typename)
        const cachedResult = cache.readFragment(fragment, {
            __typename: typename,
            id: contentId,
        })
        if (cachedResult) {
            cache.writeFragment(fragment, {
                id: contentId,
                comments: {
                    __typename: 'CommentsConnection',
                    totalCount: cachedResult.comments.totalCount + 1,
                },
            })
        }
    },
    commentDeleted: (result: CommentDeletedSubscription, _args, cache) => {
        const {
            comment: { id, contentType, contentId },
        } = result.commentDeleted
        cache.invalidate({
            __typename: 'Comment',
            id,
        })
        const typename = contentItemTypeToTypename[contentType]
        const fragment = getCommentsTotalCountFragment(typename)
        const cachedResult = cache.readFragment(fragment, {
            __typename: typename,
            id: contentId,
        })
        if (cachedResult) {
            cache.writeFragment(fragment, {
                id: contentId,
                comments: {
                    __typename: 'CommentsConnection',
                    totalCount: Math.max(
                        cachedResult.comments.totalCount - 1,
                        0,
                    ),
                },
            })
        }
    },
    commentUpdated: (result: CommentUpdatedSubscription, _args, cache) => {
        const {
            comment: { textBody, id: commentId },
        } = result.commentUpdated
        cache.writeFragment(
            graphql(`
              fragment _ on Comment {
                id
                textBody
                isUpdated
              }
            `),
            { id: commentId, textBody, isUpdated: true },
        )
    },
    commentReactionsUpdated: (
        result: CommentReactionsUpdatedSubscription,
        _args,
        cache,
    ) => {
        const { contentId: id } = result.commentReactionsUpdated
        cache.invalidate({
            __typename: 'Comment',
            id,
        })
        cache.invalidate({
            __typename: 'ChatMessage',
            id,
        })
    },
}
